import { useEffect, useContext } from "react";
import { fromJS, List } from "immutable";
import get from "lodash/get";
import { ContextStore } from "./Reducer";
import moment from "moment";
import axios from "axios";
import { useHistory, useLocation } from "react-router-dom";
import { noop } from "lodash";

const isProduction = process.env.NODE_ENV === "production";
// const isProduction = true

const addPath = (path) => `/api/lobby${path}`;

const service = axios.create();

function checkAuth({ error_code, error_msg, callback = noop, dispatch }) {
  if (isProduction) {
    // auth fail
    if (error_code === 14001 || error_code === 4013 || error_code === 4011) {
      console.error(error_msg);
      return dispatch({
        type: "CHANGE_DATA",
        path: "isAuth",
        value: false,
      });
    }
    // maintain
    if (error_code === 1016)
      return dispatch({
        type: "CHANGE_DATA",
        path: "isMaintain",
        value: true,
      });
  }

  callback();
}

service.interceptors.response.use(
  (res) => {
    const result = fromJS(get(res, ["data", "result"]));
    const error_code = get(res, ["data", "error_code"]);
    const error_msg = get(res, ["data", "error_msg"]);
    return { result, error_code, error_msg };
  },
  (error) => {
    const { response } = error;
    return Promise.reject(response);
  }
);

export const useFetchAuth = () => {
  const {
    dispatch,
    state: { stateReducer },
  } = useContext(ContextStore);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const lobby_token = query.get("token");
  const history = useHistory();
  useEffect(() => {
    getAuthToken(lobby_token, dispatch, history, stateReducer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, lobby_token]);
};

export const useGetApiData = () => {
  const {
    state: { stateReducer },
    dispatch,
  } = useContext(ContextStore);
  const getAuth = stateReducer.get("getAuth");
  const isAuth = stateReducer.get("isAuth");

  const lang = stateReducer.get("lang");
  useEffect(() => {
    if (getAuth && isAuth) {
      dispatch({ type: "CHANGE_DATA", path: "isAuth", value: true });
      getUserInfo(dispatch);
      getGameList(lang, dispatch);
      getLangList(dispatch);
      getAnnouncementList(dispatch);
      // getNewstickerList(dispatch)
      getTypeList(dispatch);
      getCornerTagList(lang, dispatch);
      getAdvertiseList(dispatch);
      getTopRtp(dispatch);
      getPlayerRank(dispatch);
      getFeaturedGames(dispatch, lang);
    }
  }, [dispatch, getAuth, isAuth, lang]);
};

// token驗證
const getAuthToken = (lobby_token, dispatch, history, stateReducer) => {
  service
    .get(addPath("/user/auth_token"), {
      params: {
        lobby_token,
      },
    })
    .then(({ error_code }) => {
      service.defaults.headers.lobbyToken = lobby_token;
      if (isProduction) {
        if (error_code === 1016)
          return dispatch({
            type: "CHANGE_DATA",
            path: "isMaintain",
            value: true,
          });
        if (error_code !== 1) {
          dispatch({ type: "CHANGE_DATA", path: "getAuth", value: false });
          return dispatch({
            type: "CHANGE_DATA",
            path: "isAuth",
            value: false,
          });
        } else {
          return dispatch({
            type: "CHANGE_DATA",
            path: "getAuth",
            value: true,
          });
        }
      }
      // set api headers default
      if (error_code !== 1) {
        dispatch({ type: "CHANGE_DATA", path: "isAuth", value: false });
        dispatch({ type: "CHANGE_DATA", path: "getAuth", value: false });
      } else {
        dispatch({ type: "CHANGE_DATA", path: "getAuth", value: true });
      }
      // dispatch({ type: "CHANGE_DATA", path: "getAuth", value: true })
    });
};

// 用戶資訊
export const getUserInfo = (dispatch) =>
  service
    .get(addPath("/user/user_info"))
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        error_code,
        dispatch,
        error_msg,
        callback: () =>
          dispatch({ type: "INIT_DATA", path: "userInfo", result }),
      })
    );

// 遊戲清單
const getGameList = (lang, dispatch) =>
  service
    .get(addPath("/game/game_list"), { params: { lang } })
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        error_code,
        dispatch,
        error_msg,
        callback: () => {
          const newResult = result.map((value) => {
            return value;
          });
          const sortList = newResult.sort((a, b) => {
            if (moment(b.get("present_time")).isSame(a.get("present_time")))
              return b.get("id") - a.get("id");
            return (
              moment(b.get("present_time")) - moment(a.get("present_time"))
            );
          });
          dispatch({ type: "INIT_DATA", path: "isLoading", result: false });
          dispatch({ type: "INIT_DATA", path: "gameList", result: sortList });
        },
      })
    );

// 語系清單
const getLangList = (dispatch) =>
  service.get(addPath("/lang/list")).then(({ result, error_code, error_msg }) =>
    checkAuth({
      error_code,
      dispatch,
      error_msg,
      callback: () => dispatch({ type: "INIT_DATA", path: "langList", result }),
    })
  );

// // 跑馬燈
// const getNewstickerList = (dispatch) =>
//   service
//     .get(addPath("/newsticker/list"))
//     .then(({ result, error_code, error_msg }) =>
//       checkAuth({
//         error_code,
//         dispatch,
//         error_msg,
//         callback: () =>
//           dispatch({ type: "INIT_DATA", path: "newsTickerList", result }),
//       })
//     )

// 遊戲類型
const getTypeList = (dispatch) =>
  service
    .get(addPath("/game/type_list"))
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        error_code,
        dispatch,
        error_msg,
        callback: () =>
          dispatch({
            type: "INIT_DATA",
            path: "typeList",
            result: result.pop(), // hide live
          }),
      })
    );

// 角標類型
const getCornerTagList = (lang, dispatch) =>
  service
    .get(addPath("/corner_tag/list_lang"), { params: { lang } })
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        error_code,
        dispatch,
        error_msg,
        callback: () => {
          const newResult = result.map((value, key) => {
            return value;
          });
          dispatch({
            type: "INIT_DATA",
            path: "cornerTagList",
            result: newResult,
          });
        },
      })
    );

// 廣告類型
const getAdvertiseList = (dispatch) =>
  service.get(addPath("/ad_guanggao/list")).then(
    ({ result, error_code, error_msg }) =>
      checkAuth({
        result,
        error_code,
        dispatch,
        error_msg,
        callback: () =>
          dispatch({
            type: "INIT_DATA",
            path: "advertise",
            result: fromJS({
              isLoading: false,
              data: result,
            }),
          }),
      }),
    (err) => List()
  );

// 公告列表
const getAnnouncementList = (dispatch) =>
  service.get(addPath("/announcement/list")).then(
    ({ result, error_code, error_msg }) =>
      checkAuth({
        result,
        error_code,
        dispatch,
        error_msg,
        callback: () =>
          dispatch({
            type: "INIT_DATA",
            path: "announcement",
            result: fromJS({
              isLoading: false,
              data: result,
            }),
          }),
      }),
    (err) => List()
  );

// 編輯最愛設定
export const putUpdateFavorite = (data, dispatch) =>
  service
    .put(addPath("/user/update_favorite"), data)
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        result,
        error_code,
        dispatch,
        error_msg,
        callback: () => getUserInfo(dispatch),
      })
    );

// 遊戲連結
export const getGameLink = ({ params, dispatch }) =>
  service
    .get(addPath("/game/game_link"), { params })
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        result,
        error_code,
        dispatch,
        error_msg,
        callback: () => {
          if (error_code === 1) {
            // 大廳params參數，調整遊戲連結參數
            const url = window.location;
            const query = new URLSearchParams(url.href);
            const app = query.get("app");
            const dollarsign = query.get("dollarsign");
            const detect = query.get("detect");
            let gameUrl = result;
            if (app === "Y") {
              gameUrl = gameUrl.replace("app=N", "app=Y");
            }
            // 不管如何預設都要N再開啟
            // if (dollarsign === null ) {
            //   gameUrl = gameUrl.replace("dollarsign=Y", "dollarsign=N")
            // }
            if (dollarsign === "N") {
              gameUrl = gameUrl.replace("dollarsign=Y", "dollarsign=N");
            }
            if (dollarsign === "Y") {
              gameUrl = gameUrl.replace("dollarsign=N", "dollarsign=Y");
            }
            if (detect === "Y") {
              gameUrl = gameUrl.replace("detect=N", "detect=Y");
            }
            gameUrl += "&leaveUrl=close";
            dispatch({
              type: "CHANGE_DATA",
              path: "gameUrl",
              value: gameUrl,
            });
          } else {
            alert(`error_msg:${error_msg}, error_code:${error_code}`);
          }
        },
      })
    );

// 更新大頭貼
export const putUpdateAvatar = (data, dispatch) =>
  service
    .put(addPath("/user/update_avatar"), data)
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        result,
        error_code,
        dispatch,
        error_msg,
        callback: () => getUserInfo(dispatch),
      })
    );

// 取得派彩指數排行
const getTopRtp = (dispatch) =>
  service
    .get(addPath("/rank/top_rtp"))
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        error_code,
        dispatch,
        error_msg,
        callback: () =>
          dispatch({ type: "INIT_DATA", path: "payoutRank", result }),
      })
    );

// 取得玩家排行
const getPlayerRank = (dispatch) =>
  service
    .get(addPath("/rank/top_win"))
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        error_code,
        dispatch,
        error_msg,
        callback: () =>
          dispatch({ type: "INIT_DATA", path: "playerRank", result }),
      })
    );

//取得精選遊戲
const getFeaturedGames = (dispatch, lang) =>
  service
    .get(addPath(`/featured_game/list?lang=${lang}`))
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        error_code,
        dispatch,
        error_msg,
        callback: () =>
          dispatch({ type: "INIT_DATA", path: "featuredGames", result }),
      })
    );

//計算遊戲點擊數

export const gameClickCount = (data) =>
  service
    .post(addPath("/game_click/create"), data)
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        result,
        error_code,
        error_msg,
      })
    );

export const memberSystemLink = (lang) =>
  service
    .post(addPath(`/links/ms_link?lang=${lang}`))
    .then(({ result, error_code, error_msg }) =>
      checkAuth({
        result,
        error_code,
        error_msg,
        callback: () => window.open(result),
      })
    );
